import PropTypes from 'prop-types';
import styles from './FJImageExtenderEdit.module.less';
import { useRef } from 'react';
import useIsMobile from '../../../../hooks/useIsMobile';

const _getClientLimit = (boardRect, imageRect, direction, curPoint) => {
    // image 最小边长
    const minLong = Math.min(boardRect.width, boardRect.height) * 0.3;

    const ratio = imageRect.width / imageRect.height;
    const imageMinWidth = ratio >= 1 ? minLong : minLong * ratio;

    switch (direction) {
        case 'right_bottom': {
            const imageMaxHeight = boardRect.height - (imageRect.y - boardRect.y);
            const imageMaxWidth = Math.min(boardRect.width - (imageRect.x - boardRect.x), imageMaxHeight * ratio);

            return {
                maxClientX: curPoint.x + (imageMaxWidth - imageRect.width),
                minClientX: curPoint.x - (imageRect.width - imageMinWidth),
            };
        }
        case 'right_top': {
            const imageMaxHeight = imageRect.height + (imageRect.y - boardRect.y);
            const imageMaxWidth = Math.min(boardRect.width - (imageRect.x - boardRect.x), imageMaxHeight * ratio);

            return {
                maxClientX: curPoint.x + (imageMaxWidth - imageRect.width),
                minClientX: curPoint.x - (imageRect.width - imageMinWidth),
            };
        }
        case 'left_bottom': {
            const imageMaxHeight = boardRect.height - (imageRect.y - boardRect.y);
            const imageMaxWidth = Math.min(imageRect.width + (imageRect.x - boardRect.x), imageMaxHeight * ratio);

            return {
                maxClientX: curPoint.x + (imageRect.width - imageMinWidth),
                minClientX: curPoint.x - (imageMaxWidth - imageRect.width),
            };
        }
        case 'left_top': {
            const imageMaxHeight = imageRect.height + (imageRect.y - boardRect.y);
            const imageMaxWidth = Math.min(imageRect.width + (imageRect.x - boardRect.x), imageMaxHeight * ratio);

            return {
                maxClientX: curPoint.x + (imageRect.width - imageMinWidth),
                minClientX: curPoint.x - (imageMaxWidth - imageRect.width),
            };
        }

        case 'content_box': {
            return {
                maxClientX: curPoint.x + boardRect.width - (imageRect.x - boardRect.x) - imageRect.width,
                maxClientY: curPoint.y + boardRect.height - (imageRect.y - boardRect.y) - imageRect.height,
                minClientX: curPoint.x - (imageRect.x - boardRect.x),
                minClientY: curPoint.y - (imageRect.y - boardRect.y),
            };
        }
        default:
            return {
                maxClientX: 0,
                maxClientY: 0,
                minClientX: 0,
                minClientY: 0,
            };
    }
};

const FJImageExtenderEdit = props => {
    const { imageRect, changeImageRect, boardRect } = props;

    const curEditDirection = useRef(null);
    const maxClient = useRef({});
    const minClient = useRef({});
    const downPoint = useRef({ x: 0, y: 0 });
    const isMobile = useIsMobile();
    const isDoubleTouch = useRef(false);

    const handleDown = (direction, e) => {
        const touches = e?.touches;
        isDoubleTouch.current = touches?.length === 2;
        if (isDoubleTouch.current) {
            return;
        }
        e.stopPropagation();

        curEditDirection.current = direction;

        const { clientX, clientY } = isMobile ? (e.targetTouches ? e.targetTouches[0] : e) : e;

        downPoint.current.x = clientX;
        downPoint.current.y = clientY;

        const { maxClientX, minClientX, maxClientY, minClientY } = _getClientLimit(
            boardRect,
            imageRect,
            direction,
            downPoint.current,
        );

        maxClient.current = {
            x: maxClientX,
            y: maxClientY,
        };
        minClient.current = {
            x: minClientX,
            y: minClientY,
        };

        window.closeSideModel && window.closeSideModel();
        bindEvent();
    };

    const handleUp = () => {
        unbindEvent();
    };

    const handleMove = e => {
        if (isDoubleTouch.current) {
            return;
        }
        e.stopPropagation();
        const { clientX, clientY } = isMobile ? (e.targetTouches ? e.targetTouches[0] : e) : e;
        const { x, y } = downPoint.current;

        const direction = curEditDirection.current;

        let newImageRect = { ...imageRect };

        let useClientX =
            clientX < minClient.current.x
                ? minClient.current.x
                : clientX > maxClient.current.x
                  ? maxClient.current.x
                  : clientX;
        let useClientY =
            clientY < minClient.current.y
                ? minClient.current.y
                : clientY > maxClient.current.y
                  ? maxClient.current.y
                  : clientY;

        switch (direction) {
            case 'right_top':
                newImageRect.width += useClientX - x;
                newImageRect.height = newImageRect.width / (imageRect.width / imageRect.height);
                newImageRect.y += imageRect.height - newImageRect.height;
                break;
            case 'left_top':
                newImageRect.width += x - useClientX;
                newImageRect.height = newImageRect.width / (imageRect.width / imageRect.height);
                newImageRect.x += useClientX - x;
                newImageRect.y += imageRect.height - newImageRect.height;
                break;
            case 'left_bottom':
                newImageRect.width += x - useClientX;
                newImageRect.height = newImageRect.width / (imageRect.width / imageRect.height);
                newImageRect.x += useClientX - x;
                break;
            case 'right_bottom':
                newImageRect.width += useClientX - x;
                newImageRect.height = newImageRect.width / (imageRect.width / imageRect.height);
                break;
            case 'content_box':
                newImageRect.x += useClientX - x;
                newImageRect.y += useClientY - y;
                break;
            default:
                break;
        }

        changeImageRect(newImageRect, direction);
    };

    const bindEvent = () => {
        if (!isMobile) {
            document.addEventListener('mousemove', handleMove);
            document.addEventListener('mouseup', handleUp);
        } else {
            document.addEventListener('touchmove', handleMove);
            document.addEventListener('touchend', handleUp);
        }
    };

    const unbindEvent = () => {
        if (!isMobile) {
            document.removeEventListener('mousemove', handleMove);
            document.removeEventListener('mouseup', handleUp);
        } else {
            document.removeEventListener('touchmove', handleMove);
            document.removeEventListener('touchend', handleUp);
        }
    };

    return (
        <div
            className={styles.edit_box}
            style={{
                width: imageRect.width,
                height: imageRect.height,
                left: imageRect.x,
                top: imageRect.y,
            }}
            onMouseDown={e => handleDown('content_box', e)}
            onTouchStart={e => handleDown('content_box', e)}
        >
            <div
                className={styles.right_top}
                onMouseDown={e => handleDown('right_top', e)}
                onTouchStart={e => handleDown('right_top', e)}
            ></div>
            <div
                className={styles.left_top}
                onMouseDown={e => handleDown('left_top', e)}
                onTouchStart={e => handleDown('left_top', e)}
            ></div>
            <div
                className={styles.left_bottom}
                onMouseDown={e => handleDown('left_bottom', e)}
                onTouchStart={e => handleDown('left_bottom', e)}
            ></div>
            <div
                className={styles.right_bottom}
                onMouseDown={e => handleDown('right_bottom', e)}
                onTouchStart={e => handleDown('right_bottom', e)}
            ></div>
        </div>
    );
};

FJImageExtenderEdit.propTypes = {
    imageRect: PropTypes.object,
    changeImageRect: PropTypes.func,
    boardRect: PropTypes.object,
};

export default FJImageExtenderEdit;
